import React, { useState } from "react";
import dayjs from "dayjs";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import Icon from "../../../common/Icon";
import PageLoader from "../../../shared/PageLoader";
import { useBulkUploadDocuments } from "../../../../backend/compliance";
import { ComplianceActivityStatus } from "../../../../constants/Compliance.constants";
import { toBackendDateFormat } from "../../../../utils/common";
import {
    validateFileWithContent,
    FILE_TYPE_GROUPS,
    FILE_SIZE_LIMITS,
    generateAcceptAttribute,
    VALIDATION_MESSAGES,
} from "../../../../utils/fileValidation";

const UPLOADABLE_STATUSES = [ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT];
const TODAY = dayjs().format('YYYY-MM-DD');

function ComplianceBulkUploadModal({ onClose, selectedItems }: any) {
    const [uploadFiles, setUploadFiles] = useState<any[]>([]);
    const [auditedDate, setAuditedDate] = useState('');
    const [formsStatusRemarks, setFormsStatusRemarks] = useState('');
    const [submitToManager, setSubmitToManager] = useState(false);

    const { bulkUploadDocuments, uploading } = useBulkUploadDocuments(
        () => {
            onClose(true);
            if (submitToManager) {
                toast.success("Compliances submitted to Compliance Manager successfully.");
            } else if (uploadFiles.length > 0) {
                toast.success("Files Uploaded Successfully to Pending Activities");
            } else {
                toast.success("Compliance details updated successfully.");
            }
        },
        (error: any) => {
            const data = (error && error.response && error.response.data) || {};
            toast.error(data.error || data.message || "File upload failed.");
            const details = data.details;
            if (details && details.length > 0) {
                details.forEach((detail: string) => toast.error(detail));
            }
        }
    );

    async function onFileChange(event: any) {
        const _uploadedFiles = [...uploadFiles];
        if (event) {
            const length = event.target.files.length;
            const time = new Date().getTime();
            for (let i = 0; i < length; i++) {
                const file = event.target.files[i];
                const validation = await validateFileWithContent(
                    file,
                    FILE_TYPE_GROUPS.ALL_DOCUMENTS,
                    FILE_SIZE_LIMITS.LARGE
                );
                _uploadedFiles.push({
                    id: time + i,
                    file,
                    invalidFile: !validation.isValid,
                    errorMessage: validation.error,
                });
            }
            setUploadFiles(_uploadedFiles);
            event.target.value = null;
        }
    }

    function onDeleteFile(index: number) {
        const _uploadedFiles = [...uploadFiles];
        _uploadedFiles.splice(index, 1);
        setUploadFiles(_uploadedFiles);
    }

    function clearAll() {
        setUploadFiles([]);
        setAuditedDate('');
        setFormsStatusRemarks('');
        setSubmitToManager(false);
    }

    function handleSubmit() {
        const eligibleItems = (selectedItems || []).filter((item: any) => UPLOADABLE_STATUSES.includes(item.status));
        if (eligibleItems.length === 0) {
            toast.warning("None of the selected compliances are in Due or Non-Compliant status.");
            return;
        }
        if (submitToManager && (!auditedDate || !formsStatusRemarks.trim())) {
            toast.warning("Actual Completion Date and Comments for Manager are required to submit to Compliance Manager.");
            return;
        }
        const formData = new FormData();
        uploadFiles.forEach((file) => formData.append("files[]", file.file));
        eligibleItems.forEach((item: any) => formData.append("compliance_ids[]", item.id));
        if (auditedDate) {
            formData.append("audited_date", toBackendDateFormat(new Date(auditedDate)) as string);
        }
        if (formsStatusRemarks.trim()) {
            formData.append("forms_status_remarks", formsStatusRemarks.trim());
        }
        formData.append("is_to_be_submitted", String(submitToManager));
        bulkUploadDocuments(formData);
    }

    const hasInvalidFile = uploadFiles.some((file) => file.invalidFile);
    const hasAnyAction = uploadFiles.length > 0 || Boolean(auditedDate) || Boolean(formsStatusRemarks.trim()) || submitToManager;
    const missingRequiredForSubmit = submitToManager && (!auditedDate || !formsStatusRemarks.trim());

    return (
        <>
            <Modal show={true} backdrop="static" animation={false} size="lg" centered={true}>
                <Modal.Header closeButton={true} onHide={() => onClose()}>
                    <Modal.Title className="bg">Bulk Upload</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label fw-medium">Select file to upload</label>
                        <input
                            type="file"
                            className="form-control"
                            onChange={onFileChange}
                            accept={generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS)}
                            multiple
                        />
                        <p className="text-muted mt-1 mb-0" style={{ fontSize: 13 }}>
                            {VALIDATION_MESSAGES.ALLOWED_FILE_ALL_DOCS}
                        </p>
                    </div>

                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #E6E6E6", borderRadius: 8 }}>
                        <table className="table modalTable fixed_header mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "8%" }}>#</th>
                                    <th style={{ width: "84%" }}>File Name</th>
                                    <th style={{ width: "8%" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadFiles.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted py-4">
                                            No files uploaded
                                        </td>
                                    </tr>
                                )}
                                {uploadFiles.map((file, index) => (
                                    <tr key={file.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <div style={{ wordBreak: "break-all" }}>{file.file.name}</div>
                                            {file.invalidFile && (
                                                <div className="text-danger" style={{ fontSize: 12 }}>
                                                    {file.errorMessage || VALIDATION_MESSAGES.INVALID_FILE_ALL_DOCS}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <Icon type="button" name="trash" text="Delete" action={() => onDeleteFile(index)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3">
                        <label className="form-label fw-medium">
                            Actual Completion Date{submitToManager && <span className="text-danger"> *</span>}
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            style={{ maxWidth: 220 }}
                            max={TODAY}
                            value={auditedDate}
                            onChange={(e) => setAuditedDate(e.target.value)}
                        />
                    </div>

                    <div className="mt-3">
                        <label className="form-label fw-medium">
                            Comments for Manager{submitToManager && <span className="text-danger"> *</span>}
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            style={{ resize: "vertical" }}
                            placeholder="Enter your comments here..."
                            value={formsStatusRemarks}
                            onChange={(e) => setFormsStatusRemarks(e.target.value)}
                        />
                    </div>

                    <div className="mt-3 form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="submitToManager"
                            checked={submitToManager}
                            onChange={(e) => setSubmitToManager(e.target.checked)}
                        />
                        <label className="form-check-label fw-medium" htmlFor="submitToManager">
                            Submit to Compliance Manager
                        </label>
                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                            When checked, marks the selected compliances as Submitted for the manager's review. Actual Completion Date and Comments for Manager become mandatory.
                        </p>
                        {
                            missingRequiredForSubmit &&
                            <p className="text-danger mb-0" style={{ fontSize: 13 }}>
                                Please fill Actual Completion Date and Comments for Manager to submit.
                            </p>
                        }
                    </div>

                    <p className="text-muted mt-3 mb-0" style={{ fontSize: 13 }}>
                        Compliances which are in Due, Non Compliant only will get Uploaded
                    </p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={() => onClose()}>Back</Button>
                    <div className="d-flex gap-2">
                        <Button variant="light" onClick={clearAll}>Reset</Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!hasAnyAction || hasInvalidFile || missingRequiredForSubmit}
                        >
                            Submit
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
            {uploading && <PageLoader />}
        </>
    );
}

export default ComplianceBulkUploadModal;
